import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Filter, SortAsc, SortDesc, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function MovieFilters({ onFilterChange, currentSort, currentGenre }) {
  const [genres, setGenres] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState(currentSort || 'release_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedGenre, setSelectedGenre] = useState(currentGenre || '');

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/genres/movies`);
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    if (onFilterChange) {
      onFilterChange({ sortBy: newSort, sortOrder, genre: selectedGenre });
    }
  };

  const handleOrderToggle = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    setSortOrder(newOrder);
    if (onFilterChange) {
      onFilterChange({ sortBy, sortOrder: newOrder, genre: selectedGenre });
    }
  };

  const handleGenreChange = (genre) => {
    setSelectedGenre(genre);
    if (onFilterChange) {
      onFilterChange({ sortBy, sortOrder, genre });
    }
  };

  const clearFilters = () => {
    setSortBy('tokrav_latest');
    setSortOrder('desc');
    setSelectedGenre('');
    if (onFilterChange) {
      onFilterChange({ sortBy: 'tokrav_latest', sortOrder: 'desc', genre: '' });
    }
  };

  return (
    <div className="mb-6">
      {/* Bouton toggle filtres */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bebas text-white">Films</h2>
        <Button
          onClick={() => setShowFilters(!showFilters)}
          className="bg-bg-secondary hover:bg-white/10 text-white"
        >
          <Filter className="w-4 h-4 mr-2" />
          {showFilters ? 'Masquer' : 'Filtres'}
        </Button>
      </div>

      {/* Panneau de filtres */}
      {showFilters && (
        <div className="bg-bg-secondary border border-white/20 rounded-lg p-4 mb-4 space-y-4">
          {/* Tri */}
          <div>
            <h3 className="text-sm font-work font-semibold text-white mb-2 uppercase tracking-wider">
              Trier par
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => handleSortChange('tokrav_latest')}
                className={`${
                  sortBy === 'tokrav_latest'
                    ? 'bg-netflix-red hover:bg-netflix-red-hover text-white'
                    : 'bg-bg-primary hover:bg-white/10 text-text-secondary'
                }`}
              >
                🆕 Derniers Ajouts
              </Button>
              <Button
                onClick={() => handleSortChange('release_date')}
                className={`${
                  sortBy === 'release_date'
                    ? 'bg-netflix-red hover:bg-netflix-red-hover text-white'
                    : 'bg-bg-primary hover:bg-white/10 text-text-secondary'
                }`}
              >
                Date de sortie
              </Button>
              <Button
                onClick={() => handleSortChange('vote_average')}
                className={`${
                  sortBy === 'vote_average'
                    ? 'bg-netflix-red hover:bg-netflix-red-hover text-white'
                    : 'bg-bg-primary hover:bg-white/10 text-text-secondary'
                }`}
              >
                Note
              </Button>
              <Button
                onClick={() => handleSortChange('title')}
                className={`${
                  sortBy === 'title'
                    ? 'bg-netflix-red hover:bg-netflix-red-hover text-white'
                    : 'bg-bg-primary hover:bg-white/10 text-text-secondary'
                }`}
              >
                Titre A-Z
              </Button>
              <Button
                onClick={handleOrderToggle}
                className="bg-bg-primary hover:bg-white/10 text-white"
                title={sortOrder === 'desc' ? 'Plus récents en premier' : 'Plus anciens en premier'}
              >
                {sortOrder === 'desc' ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Genres */}
          <div>
            <h3 className="text-sm font-work font-semibold text-white mb-2 uppercase tracking-wider">
              Genre
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => handleGenreChange('')}
                className={`${
                  selectedGenre === ''
                    ? 'bg-netflix-red hover:bg-netflix-red-hover text-white'
                    : 'bg-bg-primary hover:bg-white/10 text-text-secondary'
                }`}
              >
                Tous
              </Button>
              {genres.map((genre) => (
                <Button
                  key={genre}
                  onClick={() => handleGenreChange(genre)}
                  className={`${
                    selectedGenre === genre
                      ? 'bg-netflix-red hover:bg-netflix-red-hover text-white'
                      : 'bg-bg-primary hover:bg-white/10 text-text-secondary'
                  }`}
                >
                  {genre}
                </Button>
              ))}
            </div>
          </div>

          {/* Bouton reset */}
          {(selectedGenre || sortBy !== 'release_date' || sortOrder !== 'desc') && (
            <div className="flex justify-end">
              <Button
                onClick={clearFilters}
                variant="outline"
                className="border-white/20 hover:border-white/40 text-text-secondary hover:text-white"
              >
                <X className="w-4 h-4 mr-2" />
                Réinitialiser
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Filtres actifs (affichage compact) */}
      {!showFilters && (selectedGenre || sortBy !== 'tokrav_latest') && (
        <div className="flex flex-wrap gap-2 mb-4 text-sm">
          <span className="text-text-secondary">Filtres actifs:</span>
          {sortBy !== 'tokrav_latest' && (
            <span className="px-2 py-1 bg-netflix-red/20 border border-netflix-red/40 rounded-full text-white">
              {sortBy === 'vote_average' ? 'Par note' : 
               sortBy === 'title' ? 'Par titre' : 
               sortBy === 'release_date' ? 'Date de sortie' :
               sortBy === 'tokrav_latest' ? 'Derniers Ajouts' : sortBy}
            </span>
          )}
          {selectedGenre && (
            <span className="px-2 py-1 bg-netflix-red/20 border border-netflix-red/40 rounded-full text-white">
              {selectedGenre}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
